import * as THREE from 'three';
import { softDot } from './Environment.js';

/**
 * Partículas sencillas (destellos de monedas, astillas, polvo…): un único THREE.Points con una
 * reserva fija y mezcla aditiva. Una partícula muerta tiene color negro, que en aditivo no se ve.
 */
export class Particles {
  constructor(scene, capacity = 360) {
    this.capacity = capacity;
    this.positions = new Float32Array(capacity * 3);
    this.colors = new Float32Array(capacity * 3);
    this.base = new Float32Array(capacity * 3);
    this.vel = new Float32Array(capacity * 3);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity).fill(1);
    this.gravity = new Float32Array(capacity);
    this.next = 0;
    this.alive = 0;

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geom.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.points = new THREE.Points(
      geom,
      new THREE.PointsMaterial({
        size: 0.28,
        map: softDot(),
        vertexColors: true,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.points.frustumCulled = false;
    this.points.name = 'Particles';
    scene.add(this.points);
  }

  /**
   * Explosión de partículas en pos.
   * color: hex · speed: m/s · up: impulso vertical extra · life: s · gravity: m/s²
   */
  burst(pos, { count = 10, color = 0xffffff, speed = 3, up = 1, life = 0.6, gravity = -9, spread = 0.2 } = {}) {
    const c = new THREE.Color(color);
    for (let n = 0; n < count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % this.capacity;
      const a = Math.random() * Math.PI * 2;
      const e = Math.random() * 2 - 1;
      const r = Math.sqrt(1 - e * e);
      const v = speed * (0.4 + Math.random() * 0.6);
      this.positions.set([pos.x + (Math.random() - 0.5) * spread, pos.y + (Math.random() - 0.5) * spread, pos.z + (Math.random() - 0.5) * spread], i * 3);
      this.vel.set([Math.cos(a) * r * v, Math.abs(e) * v + up, Math.sin(a) * r * v], i * 3);
      const k = 0.7 + Math.random() * 0.3;
      this.base.set([c.r * k, c.g * k, c.b * k], i * 3);
      this.maxLife[i] = this.life[i] = life * (0.6 + Math.random() * 0.4);
      this.gravity[i] = gravity;
    }
    this.alive = this.capacity;
  }

  update(dt) {
    if (!this.alive) return;
    let alive = 0;
    const { positions: p, colors: c, vel: v, base: b, life, maxLife, gravity } = this;
    const drag = Math.exp(-2.5 * dt);
    for (let i = 0; i < this.capacity; i++) {
      if (life[i] <= 0) continue;
      life[i] -= dt;
      const j = i * 3;
      if (life[i] <= 0) {
        c[j] = c[j + 1] = c[j + 2] = 0;
        continue;
      }
      alive++;
      v[j + 1] += gravity[i] * dt;
      v[j] *= drag;
      v[j + 2] *= drag;
      p[j] += v[j] * dt;
      p[j + 1] += v[j + 1] * dt;
      p[j + 2] += v[j + 2] * dt;
      const f = life[i] / maxLife[i];
      c[j] = b[j] * f;
      c[j + 1] = b[j + 1] * f;
      c[j + 2] = b[j + 2] * f;
    }
    this.alive = alive;
    this.points.geometry.attributes.position.needsUpdate = true;
    this.points.geometry.attributes.color.needsUpdate = true;
  }

  clear() {
    this.life.fill(0);
    this.colors.fill(0);
    this.alive = 0;
    this.points.geometry.attributes.color.needsUpdate = true;
  }
}
